import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@vfuk/core-grid";
import Spacing from "@vfuk/core-spacing";
import manageAccessBanner from "@img/manageAccessBanner.png";
import InfiniteScroll from "react-infinite-scroll-component";
import Container from "@vfuk/core-container";
import {
  SectionBanner,
  TableHeaderRow,
  TableRowLoader,
  NoResultsRow,
  Crumbs,
  Stack,
  TableActionButtons,
  useDebounce,
} from "@ucchub/ui";
import DashShell from "@components/common/DashShell";
import breadCrumbs from "@json/common/crumbs.json";
import ManageAccessLayout from "@components/layout/ManageAccessLayout";
import CustomersRow from "./CustomersRow/CustomersRow";
import SearchInput from "@vfuk/core-search-input";
import useCustomerList from "@ucchub/ui/src/cloudNumbers/hooks/customer/useCustomerList";
import { useNavigate } from "react-router-dom";
import useCustomerPermissions from "@hooks/usePermission";

const Customers = () => {
  const { t } = useTranslation();
  const { permissions, isPermissionLoading } = useCustomerPermissions();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    isCustomerListLoading,
    customerList,
    fetchCustomerList,
    error: customerError,
    hasMorePagesToFetch,
    clearCustomerList,
    currentPage,
  } = useCustomerList();
  const navigate = useNavigate();

  const debouncedSearchTerm = useDebounce<string>(searchQuery, 200);

  useEffect(() => {
    clearCustomerList();
    fetchCustomerList({ search: searchQuery, page: 0 });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFetchMoreData = () => {
    fetchCustomerList({ search: searchQuery, page: currentPage + 1 });
  };
  return (
    <DashShell>
      <div>
        <Crumbs items={breadCrumbs.users} />
        <SectionBanner
          heading={t("shared:users:title")}
          subtitle={
            t("shared:users:subtitle") || "User details, roles and customers"
          }
          image={manageAccessBanner}
          iconName="users"
        />
        <ManageAccessLayout activeTab="/customers" />
        <Spacing spacingLevel={{ bottom: 7 }}>
          <Container appearance="secondary">
            <Grid maxWidth="full">
              <Stack justifyContent="space-between">
                <SearchInput
                  textInput={{
                    id: "customers-search",
                    placeholder: t("core:customers:search_info") || "Search...",
                    value: searchQuery,
                    onChange: ({ target }) => setSearchQuery(target.value),
                  }}
                  fieldWrapper={{
                    width: "default",
                    label: "Search users",
                    showLabel: false,
                  }}
                  searchIconButton={{
                    onClick: () => null,
                  }}
                  onClear={() => setSearchQuery("")}
                />
                {!isPermissionLoading && permissions?.manageCustomers && (
                  <TableActionButtons
                    actions={[
                      {
                        text: t("core:customers:add_customer"),
                        icon: {
                          name: "plus",
                          justify: "left",
                        },
                        onClick: () => navigate("/customers/add-new"),
                      },
                    ]}
                  />
                )}
              </Stack>
              <div role="table">
                <TableHeaderRow
                  headers={["Customer name", "Country", "Status", "Products"]}
                />
                <InfiniteScroll
                  dataLength={customerList?.length || 0}
                  next={handleFetchMoreData}
                  hasMore={hasMorePagesToFetch}
                  className="infiniteScroll-overflow"
                  loader={
                    <TableRowLoader
                      rowCount={1}
                      colCount={4}
                      colWidths={["50%", "50%", "30%", "75%"]}
                    />
                  }
                >
                  {customerList &&
                    customerList.length > 0 &&
                    !isPermissionLoading &&
                    customerList.map((customer, i) => (
                      <CustomersRow key={i} customer={customer} />
                    ))}
                </InfiniteScroll>
                {isCustomerListLoading && (
                  <TableRowLoader
                    rowCount={1}
                    colCount={4}
                    colWidths={["50%", "50%", "30%", "75%"]}
                  />
                )}
                {!isCustomerListLoading && customerList?.length === 0 && (
                  <NoResultsRow
                    text={
                      customerError !== undefined && customerError !== ""
                        ? customerError
                        : t("core:customers:no_customers")
                    }
                  />
                )}
              </div>
            </Grid>
          </Container>
        </Spacing>
      </div>
    </DashShell>
  );
};

export default Customers;
